"use client";

import { FC } from "react";
import Image from "next/image";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@workspace/ui/components";
import { cn } from "@workspace/ui/lib/utils";

type Device = {
  id: string;
  label: string;
};

type DeviceSelectProps = {
  type: "audioinput" | "audiooutput" | "videoinput";
  value: string;
  devices: Device[];
  onValueChange: (value: string) => void;
  placeholder?: string;
  className?: string;
};

const deviceIcons: Record<DeviceSelectProps["type"], string> = {
  audioinput: "/icons/mic.svg",
  audiooutput: "/icons/speaker.svg",
  videoinput: "/icons/cam.svg",
};

const devicePlaceholders: Record<DeviceSelectProps["type"], string> = {
  audioinput: "Microphone",
  audiooutput: "Speakers",
  videoinput: "Camera",
};

export const DeviceSelect: FC<DeviceSelectProps> = ({
  type,
  value,
  devices,
  onValueChange,
  placeholder,
  className,
}) => {
  const iconSrc = deviceIcons[type];
  const label =
    devices.find((d) => d.id === value)?.label ||
    placeholder ||
    devicePlaceholders[type];

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        className={cn(
          "flex items-center gap-2.5 data-[size=default]:h-12 w-full px-5 py-[13px] rounded-xl border-2 border-[#e3d8c4] bg-white hover:bg-[#f3e4c9] hover:shadow-[0px_4px_4px_0px_rgba(41,37,36,0.12)] transition-all focus:ring-0 [&>svg:last-child]:hidden",
          className,
        )}
      >
        <Image
          src={iconSrc}
          width={20}
          height={20}
          alt={type}
          className="shrink-0"
        />
        <span className="text-lg font-semibold text-[#292524] truncate flex-1 text-left">
          {label}
        </span>
        <Image
          src="/icons/down-small.svg"
          width={20}
          height={20}
          alt="expand"
          className="shrink-0"
        />
      </SelectTrigger>
      <SelectContent className="flex flex-col gap-5 p-5 rounded-[32px] bg-white border border-[#e3d8c4] shadow-[0px_4px_4px_0px_rgba(41,37,36,0.12)]">
        {devices.map((d, i) => (
          <SelectItem
            key={i}
            className="p-[13px] rounded-xl text-lg font-semibold data-[state=checked]:bg-[#fff3dc] focus:bg-transparent hover:bg-[#f3e4c9]"
            value={d.id}
          >
            {d.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
